import { Download, FileText, MessageSquare, Sheet as SheetIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/cn";

export type AddUsersSource = "csv" | "sheet" | "slack";

interface SourceCardProps {
  active?: boolean;
  title: string;
  description: string;
  icon: ReactNode;
  onClick: () => void;
}

function SourceCard({ title, description, icon, active, onClick }: SourceCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-start gap-3 rounded-md border border-border bg-surface-elevate p-4 text-left transition-colors hover:bg-surface-subtle",
        active && "border-violet-300 bg-pulse-soft",
      )}
    >
      <span className="rounded-md bg-pulse-soft p-2 text-text-brand-strong">{icon}</span>
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold text-text-primary">{title}</span>
        <span className="text-xs text-text-secondary">{description}</span>
      </span>
    </button>
  );
}

interface AddUsersSourcePickerProps {
  onPick: (source: AddUsersSource) => void;
  onDownloadSample: () => void;
  /** Onboarding opens the wizard from an overview it can return to. */
  onBackToIntro?: () => void;
}

/// The sample sits on this screen, not only behind the CSV card, so people
/// see the expected columns before they choose between a file and a sheet.
export function AddUsersSourcePicker({
  onPick,
  onDownloadSample,
  onBackToIntro,
}: AddUsersSourcePickerProps) {
  return (
    <div className="flex flex-col gap-4">
      {onBackToIntro ? (
        <div className="flex justify-start">
          <Button type="button" variant="outline" size="lg" onClick={onBackToIntro}>
            Back to overview
          </Button>
        </div>
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col justify-center gap-3">
        <SourceCard
          title="Upload a CSV"
          description="Pick a file from your computer. You'll map columns next."
          icon={<FileText className="size-4" />}
          onClick={() => onPick("csv")}
        />
        <SourceCard
          title="Google Sheets"
          description="Paste a share link. The sheet must be 'Anyone with the link can view'."
          icon={<SheetIcon className="size-4" />}
          onClick={() => onPick("sheet")}
        />
        <SourceCard
          title="Slack workspace"
          description="Sync members from Slack (requires the Integrations module)."
          icon={<MessageSquare className="size-4" />}
          onClick={() => onPick("slack")}
        />
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-border bg-surface-subtle px-3 py-2 text-xs">
        <span className="text-text-secondary">
          Not sure which columns to use? The sample works for a CSV upload or a Google Sheet.
        </span>
        <button
          type="button"
          onClick={onDownloadSample}
          className="inline-flex items-center gap-1 font-medium text-text-brand-strong underline-offset-2 hover:underline"
        >
          <Download className="size-3" aria-hidden="true" />
          Download sample CSV
        </button>
      </div>
    </div>
  );
}
